<template>
  <notification
    type="reaction"
    :notification="notification"
    @notification-click="$emit('notification-click', $event)"
  >
    <template slot="context-content" v-if="showPreview">
      <div class="mt-3 text-content">
        <field-change-phrase :event="notification.data.event" v-if="isFieldChangeNotification" />
        <div class="text-secondary" v-else-if="isCreationEvent">
          <span class="bold" v-no-capture>{{ actor }}</span
          >&nbsp;<span>created</span>&nbsp;
          <span
            >a new <span class="bold">{{ createdEntity }}</span></span
          >
        </div>
        <checkin-values
          :checkin="notification.data.event.checkin"
          :kr="notification.data.event.keyResultSnapshot"
          v-if="eventType === GOAL_EVENT_TYPE.CHECKIN"
        />
        <div>
          <deleted-entity v-if="isContextDeleted" />
          <template v-else>
            {{ textContent }}
          </template>
        </div>
      </div>
    </template>
  </notification>
</template>

<script>
import CheckinValues from '@/components/CheckinValues'
import FieldChangePhrase from '@/components/notifications/FieldChangePhrase'
import DeletedEntity from '@/components/notifications/DeletedEntity'
import { EVENT_OTYPE } from '@/constants/awards'
import { GOAL_EVENT_TYPE } from '@/constants/goal'
import { INITIATIVE_EVENT_TYPE } from '@/constants/initiative'
import * as R from 'ramda'
import Notification from '@/components/notifications/Notification'
import convertHTMLToPlainText from '@/utils/convertHTMLToPlainText'

const notificationTypes = {
  initiativeDescriptionMention: 'initiative-description-mention',
  initiativeCommentMention: 'initiative-comment-mention',
  goalCommentMention: 'goal-comment-mention'
}

/**
 * This is the component for rendering a reaction's notification
 */
export default {
  name: 'ReactionNotification',
  props: {
    notification: Object,
    showPreview: Boolean
  },
  components: {
    Notification,
    FieldChangePhrase,
    CheckinValues,
    DeletedEntity
  },
  data() {
    return {
      GOAL_EVENT_TYPE
    }
  },
  computed: {
    isContextDeleted() {
      switch (this.notification.type) {
        case notificationTypes.initiativeDescriptionMention:
          return !this.notification.data.initiative
        default:
          return !this.notification.data.event
      }
    },
    // Path at which markup(comment/description) will be found in notification object
    markupPath() {
      switch (this.notification.type) {
        case notificationTypes.initiativeDescriptionMention:
          return ['data', 'initiative', 'description']
        default:
          return ['data', 'event', 'comment']
      }
    },
    textContent() {
      const markup = R.path(this.markupPath, this.notification)
      return convertHTMLToPlainText(markup)
    },
    eventType() {
      // For an event whose corresponding goal, kr, initiative has been deleted, event comes as null
      return this.notification.data.event?.type
    },
    isFieldChangeNotification() {
      return this.eventType === INITIATIVE_EVENT_TYPE.FIELD_CHANGED
    },
    actor() {
      const actor = this.$store.getters.userById(this.notification.data.event.actorId)
      return actor?.displayName
    },
    isCreationEvent() {
      return [
        GOAL_EVENT_TYPE.KR_CREATED,
        GOAL_EVENT_TYPE.CREATED,
        INITIATIVE_EVENT_TYPE.CREATED
      ].includes(this.eventType)
    },
    createdEntity() {
      const event = this.notification.data.event
      if (this.eventType === GOAL_EVENT_TYPE.KR_CREATED) {
        return 'KR'
      } else if (this.eventType === GOAL_EVENT_TYPE.CREATED) {
        if (event?.otype === EVENT_OTYPE.GOAL_EVENT) {
          return 'goal'
        } else if (event?.otype === EVENT_OTYPE.INITIATIVE_EVENT) {
          return 'initiative'
        }
      }
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

/deep/ .text-content {
  @include lineClamp(2);
}
</style>
